import { Col, Form, Row } from 'antd';
import { t } from 'i18next';
import moment from 'moment';
import { memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import FormDatePicker from '../../../../../../components/Inputs/FormDatePicker';
import FormInput from '../../../../../../components/Inputs/FormInput';
import HalIncotermSelector from '../../../../../../components/Selectors/HalIncoTermSelector';
import PSLSelector from '../../../../../../components/Selectors/PSLSelector';
import TransportationGroupSelector from '../../../../../../components/Selectors/TransportationGroupSelector';
import UrgencySelector from '../../../../../../components/Selectors/UrgencySelector';
import { transportationGroupData } from '../../../../constants';
import {
  amiRules,
  deliveryDateRules,
  fields,
  incotermnamedPlaceRules,
  poRules,
  shipDateRules,
  shipmentRules,
  transportationGroupRules,
  urgencyRules,
} from './constants';

const ShippingTermsSearchForm = () => {
  const [form] = Form.useForm();
  const shipDateValue = Form.useWatch('shipDate', form);
  const shipmentValue = Form.useWatch('shipment', form);
  const poValue = Form.useWatch('po', form);
  const amiValue = Form.useWatch('ami', form);

  const { data = {} } = useSelector((state) => state.shipment.shipmentDetails);
  const copyQuoteKey = useSelector((state) => state.common.copyQuoteKey);

  useEffect(() => {
    if (copyQuoteKey) {
      form.setFields([
        ...fields.map((name) => ({ name, value: data?.[name] })),
        { name: 'deliveryDate', value: moment(data?.deliveryDate) },
        { name: 'shipDate', value: moment(data?.shipDate) },
        {
          name: 'transportationGroup',
          value: transportationGroupData.find(
            (group) => group.value.toLowerCase() === data?.transportationGroup?.toLowerCase(),
          )?.id,
        },
      ]);
    }
  }, []);

  return (
    <>
      <Form form={form} name="shippingTermsSearchForm">
        <Row gutter={30}>
          <Col className="w-20">
            <TransportationGroupSelector
              name="transportationGroup"
              label={t('translation:default.fields.transportationGroup.label')}
              margin="m-0"
              editMode
              rules={transportationGroupRules}
            />
          </Col>
          <Col className="w-20">
            <FormDatePicker
              name="shipDate"
              label={t('translation:default.fields.shipDate.label')}
              margin="m-0"
              rules={shipDateRules(t)}
              disabledTargetDate={Date.now()}
            />
          </Col>
          <Col className="w-20">
            <HalIncotermSelector
              label={t('translation:getQuote.shippingTermsSearch.incoterms.label')}
              margin="m-0"
              name="incoterms"
              editMode
            />
          </Col>
          <Col className="w-20">
            <PSLSelector margin="m-0" />
          </Col>
          <Col className="w-20">
            <FormInput
              name="shipment"
              label={t('translation:getQuote.shippingTermsSearch.shipment.label')}
              margin="m-0"
              editMode
              rules={shipmentRules(amiValue, poValue)}
              validateTrigger="onChange"
              maxLength="100"
            />
          </Col>
        </Row>

        <Row gutter={30} className="mt-2">
          <Col className="w-20">
            <UrgencySelector name="urgency" rules={urgencyRules} />
          </Col>
          <Col className="w-20">
            <FormDatePicker
              name="deliveryDate"
              label={t('translation:default.fields.deliveryDate.label')}
              margin="m-0"
              rules={deliveryDateRules(t, shipDateValue)}
              disabledTargetDate={shipDateValue || Date.now()}
            />
          </Col>
          <Col className="w-20">
            <FormInput
              name="incotermnamedPlace"
              label={t('translation:shipmentAdd.shipmentDetails.incotermNamedPlace.label')}
              margin="m-0"
              editMode
              maxLength="100"
              rules={incotermnamedPlaceRules}
            />
          </Col>
          <Col className="w-20">
            <FormInput
              name="ami"
              label={t('translation:getQuote.shippingTermsSearch.ami.label')}
              margin="m-0"
              editMode
              rules={amiRules(poValue, shipmentValue)}
              validateTrigger="onChange"
              maxLength="20"
            />
          </Col>
          <Col className="w-20">
            <FormInput
              name="po"
              label={t('translation:getQuote.shippingTermsSearch.po.label')}
              margin="m-0"
              editMode
              rules={poRules(amiValue, shipmentValue)}
              validateTrigger="onChange"
              maxLength="100"
            />
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default memo(ShippingTermsSearchForm);
